import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Logo } from '../components/Logo'
import { ArrowRight } from 'lucide-react'

const posts = [
  {
    title: 'The Art of the Lowkey Flex',
    summary: 'How to post a photo that says you\'re winning without saying you\'re winning.',
    tags: ['#Aesthetic', '#Instagram', '#Growth'],
    color: '#D1D1F7',
    glow: 'rgba(209,209,247,0.2)',
    slug: '/blog/art-of-the-lowkey-flex'
  },
  {
    title: 'Why Your Captions Are Ruining Your Engagement',
    summary: 'Short vs. Long — which one does the algorithm actually like?',
    tags: ['#Algorithm', '#Strategy', '#Engagement'],
    color: '#e040fb',
    glow: 'rgba(224,64,251,0.2)',
    slug: '/blog/instagram-engagement-tips'
  },
  {
    title: 'The Unhinged Trend: Real is the New Aesthetic',
    summary: 'Why being a little chaotic is the fastest way to stop the scroll.',
    tags: ['#Trends', '#Culture', '#GenZ'],
    color: '#8E8FFA',
    glow: 'rgba(142,143,250,0.2)',
    slug: '/blog/unhinged-captions-trend'
  }
]

export function Blog() {
  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #050505 0%, #0a0a0f 100%)', fontFamily: "'Space Grotesk', 'Inter', sans-serif", overflowX: 'hidden' }}>
      <Helmet>
        <title>Blog - Social Media Lore & Strategy | The Caption Studio</title>
        <meta name="description" content="Tips, trends, and tactics for the chronically online creator. Learn the art of the lowkey flex, engagement hacks, and the unhinged trend." />
        <link rel="canonical" href="https://thecaption.studio/blog" />
      </Helmet>

      {/* Ambient glows */}
      <div style={{ position: 'fixed', top: '10%', left: '-10%', width: '500px', height: '500px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(124,58,237,0.08) 0%, transparent 70%)', filter: 'blur(80px)', pointerEvents: 'none', zIndex: 0 }} />
      <div style={{ position: 'fixed', top: '40%', right: '-10%', width: '500px', height: '500px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(224,64,251,0.08) 0%, transparent 70%)', filter: 'blur(80px)', pointerEvents: 'none', zIndex: 0 }} />

      {/* Header */}
      <header style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '12px 24px' }}>
        <nav style={{ maxWidth: '1100px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/"><Logo /></Link>
          <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
            <Link to="/about" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '0.9rem' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#ffffff')}
              onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.6)')}>About</Link>
            <Link to="/blog" style={{ color: '#e040fb', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 500 }}>Blog</Link>
            <Link to="/generate" style={{ background: 'linear-gradient(90deg, #FF00FF 0%, #8E8FFA 100%)', color: '#ffffff', textDecoration: 'none', padding: '8px 18px', borderRadius: '999px', fontSize: '0.85rem', fontWeight: 600, boxShadow: '0 0 15px rgba(212,0,255,0.3)' }}>Try It Free</Link>
          </div>
        </nav>
      </header>

      {/* Hero */}
      <main style={{ maxWidth: '800px', margin: '0 auto', padding: '80px 24px 60px', position: 'relative', zIndex: 1 }}>
        <h1 style={{ fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 800, color: '#ffffff', lineHeight: 1.15, marginBottom: '12px', textAlign: 'center' }}>
          Social Media <span style={{ background: 'linear-gradient(135deg, #e040fb 0%, #8E8FFA 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Lore & Strategy.</span>
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', marginBottom: '56px', fontSize: '1rem' }}>Tips, trends, and tactics for the chronically online creator.</p>

        {/* Blog posts */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '64px' }}>
          {posts.map((post, i) => (
            <Link key={i} to={post.slug} style={{ textDecoration: 'none' }}>
            <div style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 100%)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '24px', padding: '32px', cursor: 'pointer', transition: 'all 0.25s ease' }}
              onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = post.color + '60'; (e.currentTarget as HTMLDivElement).style.boxShadow = `0 0 24px ${post.glow}` }}
              onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(255,255,255,0.1)'; (e.currentTarget as HTMLDivElement).style.boxShadow = 'none' }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
                {post.tags.map((tag, ti) => (
                  <span key={ti} style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${post.color}40`, color: post.color, borderRadius: '999px', padding: '3px 12px', fontSize: '0.75rem', fontWeight: 600 }}>{tag}</span>
                ))}
              </div>
              <h2 style={{ color: '#ffffff', fontSize: '1.3rem', fontWeight: 700, marginBottom: '10px', lineHeight: 1.3 }}>{post.title}</h2>
              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '20px' }}>{post.summary}</p>
              <span style={{ color: post.color, fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                Read more <ArrowRight size={14} />
              </span>
            </div>
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '20px', fontSize: '0.95rem' }}>Ready to put the strategy into action?</p>
          <Link to="/generate" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'linear-gradient(90deg, #FF00FF 0%, #8E8FFA 100%)', color: '#ffffff', textDecoration: 'none', padding: '16px 36px', borderRadius: '999px', fontSize: '1.05rem', fontWeight: 700, boxShadow: '0 0 25px rgba(212,0,255,0.5)' }}>
            Generate Captions <ArrowRight size={18} />
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: '32px 24px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {[{ to: '/about', label: 'About' }, { to: '/blog', label: 'Blog' }, { to: '/privacy', label: 'Privacy' }, { to: '/terms', label: 'Terms' }].map(l => (
            <Link key={l.to} to={l.to} style={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none', fontSize: '0.85rem' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#e040fb')}
              onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.4)')}>{l.label}</Link>
          ))}
        </div>
        <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.8rem' }}>© 2026 The Caption Studio. Made with ❤️ in India</p>
      </footer>
    </div>
  )
}
